import { useState, useCallback, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNavigate } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { Edit, Search } from "lucide-react";
import {
  Table as UiTable,
  TableBody as UiTableBody,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
  TableHeader as UiTableHeader,
  TableRow as UiTableRow,
} from "../ui/table";
import Layout from "../Layout";
import navigationRoutes from "../../utils/navigationRoutes";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { formatDate } from "../../utils/dateFormatter";

const PP_RehabEstimator: React.FC = () => {
  const navigate = useNavigate();

  const schemaName = "Property Master";
  const rehabEstimateSchemaName = "Rehab Estimate";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>("");

  // Fetch Rehab Estimate schema columns
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", rehabEstimateSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          rehabEstimateSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!rehabEstimateSchemaName,
    retry: false,
  });

  // Fetch all rehab estimate data to check which properties have rehab estimates
  const { data: rehabEstimateData } = useQuery({
    queryKey: [
      "rehab-estimate-all",
      rehabEstimateSchemaName,
      rehabEstimateColumnData,
    ],
    queryFn: async () => {
      if (!rehabEstimateColumnData?.grid) {
        return { results: [], count: 0 };
      }
      const allColumns = rehabEstimateColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns
          .map((col: string) => col.toLowerCase())
          .includes("propertymaster_id")
      ) {
        allColumns.push("propertymaster_id");
      }
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          rehabEstimateSchemaName,
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle different response formats
        if (Array.isArray(response)) {
          return { results: response, count: response.length };
        }
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        console.error("Error fetching rehab estimate:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!rehabEstimateSchemaName && !!rehabEstimateColumnData,
    retry: false,
  });

  // Create a Set of property IDs that have rehab estimates
  const propertiesWithRehabEstimate = useMemo(() => {
    // Handle different response formats
    let rehabEstimates: any[] = [];
    if (Array.isArray(rehabEstimateData)) {
      rehabEstimates = rehabEstimateData;
    } else if (rehabEstimateData?.results) {
      rehabEstimates = rehabEstimateData.results;
    } else if (rehabEstimateData?.data) {
      rehabEstimates = rehabEstimateData.data;
    }

    const propertyIds = new Set<string>();

    rehabEstimates.forEach((rehab: any) => {
      // Normalize keys to lowercase for case-insensitive lookup
      const normalizedRehab: any = {};
      Object.keys(rehab).forEach(key => {
        normalizedRehab[key.toLowerCase()] = rehab[key];
      });

      // Try multiple field name variations
      const propertyId =
        normalizedRehab.propertymaster_id ??
        normalizedRehab.propertymasterid ??
        normalizedRehab.property_master_id ??
        rehab.propertymaster_id ??
        rehab.propertymaster_Id ??
        rehab.PropertyMasterId ??
        rehab.propertymasterId ??
        rehab.property_master_id ??
        rehab.propertyMasterId ??
        rehab.propertyMaster_ID;

      if (propertyId) {
        const idString = String(propertyId);
        propertyIds.add(idString);
      }
    });

    return propertyIds;
  }, [rehabEstimateData]);

  // const handleDelete = useCallback(async (id: string) => {
  //   try {
  //     await DynamicModuleService.deleteItem(schemaName, id);
  //     notify.success("Property deleted successfully!");
  //     queryClient.invalidateQueries({
  //       queryKey: ["moduleData"],
  //       exact: false
  //     });
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     notify.error("Failed to delete property");
  //   }
  // }, [schemaName, queryClient]);

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.EDIT_REHAB_ESTIMATOR.replace(":id", id));
  };

  const handleExportToExcel = useCallback(async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      toast.error("Please select at least one property to export");
      return;
    }

    try {
      toast.loading("Exporting properties to Excel...", { id: "export-excel" });

      // Get Rehab Estimate schema columns
      const rehabEstimateColumnData = await SchemaService.getColumnGridData(
        rehabEstimateSchemaName
      );
      const allRehabColumns =
        rehabEstimateColumnData?.grid?.map((field: any) => field.field_name) ??
        [];
      if (
        !allRehabColumns.map((col: string) => col.toLowerCase()).includes("id")
      ) {
        allRehabColumns.push("id");
      }

      // Fetch all selected properties by their IDs
      const propertiesResponses = await Promise.all(
        selectedRowKeys.map(id =>
          DynamicModuleService.getItemById(schemaName, String(id))
        )
      );

      // Extract property data from responses (handle different response structures)
      const propertiesData = propertiesResponses.map((response: any) => {
        // Handle different response structures
        return response?.data ?? response ?? {};
      });

      // Fetch Rehab Estimate data for each property
      const rehabEstimateDataMap = new Map<string, any>();

      await Promise.all(
        selectedRowKeys.map(async propertyId => {
          try {
            const rehabResponse = await DynamicModuleService.getGridCoByConfig(
              rehabEstimateSchemaName,
              1,
              1000,
              allRehabColumns,
              {
                propertymaster_id: String(propertyId),
              }
            );

            const rehabData =
              rehabResponse?.results?.[0] ??
              rehabResponse?.data?.[0] ??
              rehabResponse?.[0] ??
              null;
            if (rehabData) {
              rehabEstimateDataMap.set(String(propertyId), rehabData);
            }
          } catch (error) {
            console.error(
              `Error fetching rehab estimate for property ${propertyId}:`,
              error
            );
          }
        })
      );

      // Define the columns to export with their display names
      const exportColumns = [
        { key: "propertyname", label: "Property Name", source: "property" },
        { key: "sqfootage", label: "Sq Footage", source: "property" },
        { key: "rehabtype", label: "Rehab Type", source: "rehab" },
        { key: "rehabestimate", label: "Rehab Estimate", source: "rehab" },
      ];

      // Prepare data for Excel
      const excelData = propertiesData.map((property: any) => {
        const propertyId = property.id ?? property.ID ?? "";
        const rehabData = rehabEstimateDataMap.get(String(propertyId)) ?? {};

        const row: any = {};
        exportColumns.forEach(col => {
          let value: any = "";

          // Get value from appropriate source
          const sourceData = col.source === "rehab" ? rehabData : property;

          // Find the field key (case-insensitive search)
          const fieldKey = Object.keys(sourceData ?? {}).find(
            key => key.toLowerCase() === col.key.toLowerCase()
          );

          if (fieldKey) {
            value = sourceData[fieldKey];
          }

          // Handle array/object values
          if (Array.isArray(value)) {
            value = value
              .map((item: any) => {
                if (typeof item === "object" && item !== null) {
                  return item.url
                    ? (item.name ?? item.id)
                    : JSON.stringify(item);
                }
                return item;
              })
              .join(", ");
          } else if (typeof value === "object" && value !== null) {
            value =
              value.url ?? value.name ?? value.id ?? JSON.stringify(value);
          }

          row[col.label] = value ?? "";
        });
        return row;
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Rehab Estimator");

      // Generate Excel file and download
      const fileName = `Rehab_Estimator_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${selectedRowKeys.length} property(ies) to Excel`,
        { id: "export-excel" }
      );
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error?.response?.data?.message ??
          "Failed to export properties to Excel",
        { id: "export-excel" }
      );
    }
  }, [selectedRowKeys, schemaName, rehabEstimateSchemaName]);

  const columns: ProColumns<any>[] = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => formatDate(record.date),
    },
    {
      title: "Property Name",
      dataIndex: "propertyname",
    },
    {
      title: "Lien Amount",
      dataIndex: "lienamount",
    },
    {
      title: "Bed",
      dataIndex: "bed",
    },
    {
      title: "Bath",
      dataIndex: "bath",
    },
    {
      title: "Sq. Footage",
      dataIndex: "sqfootage",
    },
    {
      title: "Status",
      width: 120,
      render: (_, record) => {
        // Try multiple variations of the ID field
        const propertyId = record.id ?? record.ID ?? record.Id ?? record._id;

        const propertyIdString = propertyId ? String(propertyId) : null;
        const hasRehabEstimate =
          propertyIdString && propertiesWithRehabEstimate.has(propertyIdString);
        const status = hasRehabEstimate ? "Completed" : "Pending";

        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              hasRehabEstimate
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      hideInTable: true,
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <Button
          key="edit"
          variant="ghost"
          size="icon"
          onClick={() => handleEdit(record.id)}
        >
          <Edit size={16} />
        </Button>,
        // <Popconfirm
        //   key="delete"
        //   title="Are you sure you want to delete this property?"
        //   onConfirm={() => handleDelete(record.id)}
        //   okText="Yes"
        //   cancelText="No"
        // >
        //   <Button variant="ghost" size="icon">
        //     <Trash size={16} />
        //   </Button>
        // </Popconfirm>,
      ],
    },
  ];

  console.log("Columns defined:", columns);
  console.log(
    "DataIndex values being sent to API:",
    columns.filter(col => col.dataIndex).map(col => col.dataIndex)
  );

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
  }, []);

  // Define columns to fetch explicitly
  const columnsToFetch = useMemo(() => {
    return [
      "id",
      "date",
      "propertyname",
      "lienamount",
      "bed",
      "bath",
      "sqfootage",
    ];
  }, []);

  // Fetch module data for table
  const { data: moduleData, isLoading: moduleLoading } = useQuery({
    queryKey: ["moduleData", schemaName, activeSearchTerm],
    queryFn: async () => {
      try {
        const pageSize = activeSearchTerm ? 1000 : 1000;

        const result = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          pageSize,
          columnsToFetch,
          undefined
        );

        // Handle different response structures
        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
      }
    },
    enabled: !!schemaName,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rawTableData = useMemo(() => {
    if (!moduleData) {
      return [];
    }
    return Array.isArray(moduleData) ? moduleData : [];
  }, [moduleData]);

  // Client-side filtering by activeSearchTerm
  const filteredTableData = useMemo(() => {
    if (!activeSearchTerm) {
      return rawTableData;
    }
    const term = activeSearchTerm.toLowerCase();
    return rawTableData.filter((item: any) => {
      const propertyName =
        item.propertyname ?? item.propertyName ?? item.PropertyName ?? "";
      return String(propertyName).toLowerCase().includes(term);
    });
  }, [rawTableData, activeSearchTerm]);

  const getIdFromRecord = (rec: any) =>
    rec.id ??
    rec.ID ??
    rec.Id ??
    rec._id ??
    (Object.keys(rec ?? {}).find(k => k.toLowerCase() === "id")
      ? rec[Object.keys(rec).find(k => k.toLowerCase() === "id") as string]
      : undefined);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredTableData
        .map((r: any) => String(getIdFromRecord(r)))
        .filter(Boolean);
      setSelectedRowKeys(allIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const idStr = String(id);
    if (checked) {
      setSelectedRowKeys(prev => Array.from(new Set([...(prev ?? []), idStr])));
    } else {
      setSelectedRowKeys(prev => (prev ?? []).filter(k => String(k) !== idStr));
    }
  };

  // Build filter object - backend doesn't support partial matching operators
  // We'll do client-side filtering instead (no server filter used)

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Rehab Estimator
          </h1>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-[#3d3d7a] bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys || selectedRowKeys.length === 0}
            >
              Export Excel
            </Button>
            {/* <Button
              variant="outline"
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90 border-[#3d3d7a]"
            >
              Upload
            </Button>
            <Button
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={() => navigate(navigationRoutes.CREATE_PROPERTY)}
            >
              + Add Property
            </Button> */}
          </div>
        </div>

        {/* Search Box */}
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by property name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                type="button"
              >
                ×
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={handleSearch}
            className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
            disabled={!searchTerm.trim()}
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          {activeSearchTerm && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <UiTable>
              <UiTableHeader>
                <UiTableRow className="bg-muted/50">
                  <UiTableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={
                        filteredTableData.length > 0 &&
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length ===
                          filteredTableData
                            .map((r: any) => String(getIdFromRecord(r)))
                            .filter(Boolean).length
                      }
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                    />
                  </UiTableHead>
                  <UiTableHead>NO.</UiTableHead>
                  <UiTableHead>Date</UiTableHead>
                  <UiTableHead>Property Name</UiTableHead>
                  <UiTableHead>Lien Amount</UiTableHead>
                  <UiTableHead>Bed</UiTableHead>
                  <UiTableHead>Bath</UiTableHead>
                  <UiTableHead>Sq. Footage</UiTableHead>
                  <UiTableHead>Status</UiTableHead>
                  <UiTableHead>ACTION</UiTableHead>
                </UiTableRow>
              </UiTableHeader>
              <UiTableBody>
                {moduleLoading ? (
                  <UiTableRow>
                    <UiTableCell colSpan={10} className="py-8 text-center">
                      Loading...
                    </UiTableCell>
                  </UiTableRow>
                ) : filteredTableData.length === 0 ? (
                  <UiTableRow>
                    <UiTableCell colSpan={10} className="py-8 text-center">
                      No properties found
                    </UiTableCell>
                  </UiTableRow>
                ) : (
                  filteredTableData.map((record: any, idx: number) => {
                    const id = String(getIdFromRecord(record) ?? idx);
                    const dateVal = formatDate(
                      record.date ?? record.Date ?? record.created_at
                    );
                    const propertyName =
                      record.propertyname ?? record.propertyName ?? "-";
                    const lienAmount =
                      record.lienamount ?? record.lienAmount ?? "-";
                    const bed = record.bed ?? "-";
                    const bath = record.bath ?? "-";
                    const sq = record.sqfootage ?? record.sqFootage ?? "-";

                    const hasRehab = propertiesWithRehabEstimate.has(
                      String(id)
                    );
                    const status = hasRehab ? "Completed" : "Pending";

                    const isSelected = (selectedRowKeys ?? []).some(
                      k => String(k) === id
                    );

                    return (
                      <UiTableRow key={id}>
                        <UiTableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e =>
                              handleSelectRow(id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                          />
                        </UiTableCell>
                        <UiTableCell>{idx + 1}</UiTableCell>
                        <UiTableCell>{dateVal}</UiTableCell>
                        <UiTableCell className="font-medium">
                          {propertyName}
                        </UiTableCell>
                        <UiTableCell>{lienAmount}</UiTableCell>
                        <UiTableCell>{bed}</UiTableCell>
                        <UiTableCell>{bath}</UiTableCell>
                        <UiTableCell>{sq}</UiTableCell>
                        <UiTableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              hasRehab
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {status}
                          </span>
                        </UiTableCell>
                        <UiTableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(id)}
                            >
                              <Edit className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </UiTableCell>
                      </UiTableRow>
                    );
                  })
                )}
              </UiTableBody>
            </UiTable>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PP_RehabEstimator;
